import { type MouseEvent, type ReactElement } from 'react';
import ContentMarkup from '@/components/content-markup';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ManualCategoriesStatus } from './default-search-viewport';

/**
 * Layout styles for the `search-suggestion-categories` content asset — merchants author
 * a horizontal row of category pills (typically via `Badge` / `Button` React markup).
 *
 * Link hrefs in the BM asset must be site-relative paths (e.g. `/category/board-shorts`,
 * `/search?q=shoes`). Do not include site/locale prefixes — navigation runs through
 * `closeAndNavigate`, which applies them via the project `useNavigate` wrapper.
 */
const SEARCH_SUGGESTION_CATEGORIES_STYLES = 'min-w-0 w-full';

export interface SearchSuggestionCategoriesProps {
    status: ManualCategoriesStatus;
    /** Resolved markup when `status` is `ready`. */
    categories?: string;
    closeAndNavigate?: (link: string) => void;
}

function getCategoryLinkHref(event: { target: EventTarget | null }): string | null {
    const target = event.target;
    if (!(target instanceof Element)) {
        return null;
    }

    const link = target.closest('a[href]');
    if (!link) {
        return null;
    }

    const href = link.getAttribute('href')?.trim();
    return href || null;
}

function SearchSuggestionCategoriesSkeleton(): ReactElement {
    const pillWidths = ['w-28', 'w-32', 'w-24', 'w-20'] as const;

    return (
        <div
            className="flex items-center gap-3 min-w-0 w-full"
            data-testid="search-suggestion-categories-skeleton"
            aria-hidden>
            <Skeleton className="h-4 w-20 shrink-0" />
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                {pillWidths.map((width) => (
                    <Skeleton key={width} className={`h-8 ${width} shrink-0 rounded-full`} />
                ))}
            </div>
        </div>
    );
}

/** Renders manual search categories from a Business Manager content asset. */
export default function SearchSuggestionCategories({
    status,
    categories,
    closeAndNavigate,
}: SearchSuggestionCategoriesProps): ReactElement | null {
    if (status === 'pending') {
        return <SearchSuggestionCategoriesSkeleton />;
    }

    if (status !== 'ready' || !categories?.trim()) {
        return null;
    }

    const categoriesMarkup = categories.trim();

    // Intercept click (mouse and keyboard Enter/Space). Viewport mousedown already
    // keeps input focus; do not navigate on mousedown alone.
    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!closeAndNavigate) {
            return;
        }

        const href = getCategoryLinkHref(event);
        if (!href) {
            return;
        }

        event.preventDefault();
        closeAndNavigate(href);
    };

    return (
        <div data-testid="search-suggestion-categories" onClick={handleClick}>
            <ContentMarkup content={categoriesMarkup} className={cn(SEARCH_SUGGESTION_CATEGORIES_STYLES)} />
        </div>
    );
}
